import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { debounce } from 'lodash';
import type { SelectionState } from '../SelectionSynchronizer';
import type { Rect } from '../../types/aha-editor';
import { type Editor, Range } from 'slate';
import { ApplicationView, useController } from '@aha-app/mvc';
import findPositionedContainer from '../../util/findPositionedContainer';
import transformRectAgainstContainer from '../../util/transformRectAgainstContainer';
import positionOfCursorInDocument from '../../util/positionOfCursorInDocument';
import SelectionName from './SelectionName';
import SelectionsController from './SelectionsController';

// S=0.5, V=0.99
const COLORS = [
  '#98fdc1',
  '#fda398',
  '#98a9fd',
  '#c7fd98',
  '#fd98e5',
  '#98fdf8',
  '#fdda98',
  '#bd98fd',
  '#98fd9f',
  '#fd98ae',
  '#98cbfd',
  '#e9fd98',
  '#f398fd',
  '#98fdd6',
  '#fdb898',
  '#9b98fd',
  '#b2fd98',
  '#fd98d0',
  '#98edfd',
  '#fdef98',
  '#d298fd',
  '#98fdb4',
  '#fd9899',
  '#98b6fd',
  '#d4fd98',
  '#fd98f1',
];

// clientRect includes borders, but absolute positioning
// doesn't. This function returns a rect that's suitable for
// absolute positioning.
export function getPaddingBox(container: HTMLElement): Rect | null {
  const containerRect = container.getBoundingClientRect() as Rect;
  if (!containerRect) return null;

  const style = window.getComputedStyle(container);
  const border = {
    left: parseInt(style.borderLeftWidth, 10),
    top: parseInt(style.borderTopWidth, 10),
    right: parseInt(style.borderRightWidth, 10),
    bottom: parseInt(style.borderBottomWidth, 10),
  };

  return {
    left: containerRect.left + border.left,
    top: containerRect.top + border.top,
    width: containerRect.width - (border.left + border.right),
    height: containerRect.height - (border.top + border.bottom),
  };
}

export function updateContainerRect(
  selectionOverlay: HTMLElement,
  setContainerRect: Dispatch<SetStateAction<Rect | null>>
) {
  const container = findPositionedContainer(selectionOverlay);
  if (container) {
    setContainerRect(getPaddingBox(container));
  }
}

interface SelectionOverlayProps {
  editor: Editor;
  selection: SelectionState;
}

export const SelectionOverlay: React.FC<SelectionOverlayProps> = ({
  editor,
  selection,
}) => {
  const controller = useController(SelectionsController);
  const [rects, setRects] = useState<Rect[]>([]);
  const [containerRect, setContainerRect] = useState<Rect | null>(null);

  const selectionOverlay = useRef<HTMLDivElement>(null);

  const updateSelectionRects = useCallback(
    (selectionOverlay: HTMLDivElement | null) => {
      let rects: Rect[] | null = null;
      try {
        rects = positionOfCursorInDocument(editor, selection.selection);
      } catch (e) {
        console.warn(e);
      }
      if (rects) {
        setRects(rects);
        updateContainerRect(selectionOverlay!, setContainerRect);
      }
    },
    [editor, selection, setRects]
  );

  useEffect(() => {
    if (!selection) return;
    updateSelectionRects(selectionOverlay.current);
  }, [selection, updateSelectionRects]);

  useEffect(() => {
    if (!selection) return;

    const boundUpdateSelectionRects = () =>
      updateSelectionRects(selectionOverlay.current);
    const debouncedUpdateSelectionRects = debounce(
      boundUpdateSelectionRects,
      100
    );
    let observer: MutationObserver;

    window.addEventListener('resize', boundUpdateSelectionRects);
    window.addEventListener('scroll', debouncedUpdateSelectionRects);

    if (selectionOverlay.current && window.MutationObserver) {
      observer = new MutationObserver(boundUpdateSelectionRects);

      // Fullscreen / focused / etc. classes will change the bounds of
      // our container. These classes are added to the
      // data-editor-editable, so watch that element for updates.
      const el = selectionOverlay.current.closest('[data-editor-editable]');
      if (el) {
        observer.observe(el, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }
    }

    return () => {
      window.removeEventListener('resize', boundUpdateSelectionRects);
      window.removeEventListener('scroll', debouncedUpdateSelectionRects);
      observer?.disconnect();
      debouncedUpdateSelectionRects.cancel();
    };
  }, [selection, updateSelectionRects]);

  const cursorWidth = 2;
  const displayName = controller.getDisplayName(selection.userId);

  if (
    !containerRect ||
    !displayName ||
    displayName.length === 0 ||
    !rects ||
    rects.length === 0
  ) {
    // Return an empty div, so we can find the parent container we're
    // relative to in componentDidMount/Update
    return <div ref={selectionOverlay} />;
  }

  const backgroundColor = COLORS[selection.selectionId % COLORS.length];

  const selectionRects = rects.map((rect, i) => {
    const { top, left, width, height } = transformRectAgainstContainer(
      rect,
      containerRect
    );

    if (width === 0) return null;

    const style: React.CSSProperties = {
      position: 'absolute',
      top,
      left,
      width,
      height,
      backgroundColor,
      opacity: 0.3,
      pointerEvents: 'none',
    };
    return (
      <div key={i} style={style} className="aha-editor-v2__selection-overlay" />
    );
  });

  const cursor: React.CSSProperties = {
    position: 'absolute',
    backgroundColor,
    width: cursorWidth,
    top: 0,
    left: 0,
    height: 0,
    pointerEvents: 'none',
  };

  if (selection.selection && Range.isBackward(selection.selection)) {
    const rect = transformRectAgainstContainer(rects[0], containerRect);
    cursor.top = rect.top;
    cursor.left = rect.left - cursorWidth / 2;
    cursor.height = rect.height;
  } else {
    const rect = transformRectAgainstContainer(
      rects[rects.length - 1],
      containerRect
    );
    cursor.top = rect.top;
    cursor.left = rect.left + rect.width - cursorWidth / 2;
    cursor.height = rect.height;
  }

  return (
    <div ref={selectionOverlay}>
      <SelectionName
        name={displayName}
        top={cursor.top}
        left={cursor.left}
        color={backgroundColor}
      />
      {selectionRects}
      <div style={cursor} className="selection-cursor" />
    </div>
  );
};

export default ApplicationView(SelectionOverlay);
